import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { formatNumber, formatPercent } from '../lib/utils';
import { getStageLabel } from '../constants/dealStages';

function formatIsoDate(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function DealTable({ title, rows, onOpenDeal }) {
  if (!rows.length) {
    return (
      <div className="rounded-md border border-dashed border-gray-200 bg-gray-50/50 px-3 py-4 text-sm text-gray-500">
        {title}: no deals in this group.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
      <div className="max-h-[min(52vh,28rem)] overflow-x-auto overflow-y-auto rounded-md border border-gray-200 [-webkit-overflow-scrolling:touch] sm:max-h-[min(50vh,32rem)] md:max-h-[min(55vh,36rem)] lg:max-h-[min(60vh,40rem)]">
        <table className="w-full min-w-[1100px] border-collapse text-left text-[11px] sm:min-w-[1200px] sm:text-xs">
          <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm">
            <tr className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              <th className="whitespace-nowrap px-2 py-2">Deal</th>
              <th className="px-2 py-2">Stage</th>
              <th className="px-2 py-2">Ccy</th>
              <th className="px-2 py-2 text-right" title="CHF revenue input to weighting">Rev in</th>
              <th className="px-2 py-2 text-right" title="CHF cost input">Cost in</th>
              <th className="px-2 py-2 text-right" title="CHF margin input">Mgn in</th>
              <th className="px-2 py-2 text-right" title="Stored forecast CHF revenue">Fcast rev</th>
              <th className="px-2 py-2 text-right">Fcast cost</th>
              <th className="px-2 py-2 text-right">Fcast mgn</th>
              <th className="px-2 py-2 text-right">p</th>
              <th className="px-2 py-2 text-right">Wtd rev</th>
              <th className="px-2 py-2 text-right">Wtd mgn</th>
              <th className="px-2 py-2 text-right" title="Portfolio CHF revenue">Port rev</th>
              <th className="px-2 py-2 text-right" title="Portfolio CHF margin">Port mgn</th>
              <th className="px-2 py-2 text-right font-semibold text-gray-800">KPI CHF</th>
              <th className="px-2 py-2 whitespace-nowrap">Deal Closure Date</th>
              <th className="px-2 py-2 text-right">Pay d</th>
              <th className="px-2 py-2 whitespace-nowrap">Stamp</th>
              <th className="px-2 py-2">FX src</th>
              <th className="px-2 py-2 text-right">EUR</th>
              <th className="px-2 py-2 text-right">USD</th>
              <th className="px-2 py-2">Basis</th>
              <th className="min-w-[140px] px-2 py-2">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.map((r) => (
              <tr
                key={r.deal_id}
                className={`tabular-nums text-gray-800 ${r.deal_id ? 'cursor-pointer hover:bg-sky-50/50' : ''}`}
                onClick={() => {
                  if (r.deal_id) onOpenDeal?.(r.deal_id);
                }}
              >
                <td className="max-w-[200px] px-2 py-1.5 align-top">
                  <div className="font-medium text-gray-900">{r.project_name}</div>
                  {r.client_name ? <div className="text-[10px] text-gray-500">{r.client_name}</div> : null}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 align-top">{getStageLabel(r.stage)}</td>
                <td className="px-2 py-1.5 align-top font-mono">{r.core_currency}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.chf_revenue_input)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.chf_cost_input)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.chf_margin_input)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.forecast_value_chf)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.forecast_cost_chf)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.forecast_margin_chf)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatPercent(r.stage_probability * 100)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.weighted_revenue_chf)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.weighted_margin_chf)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.portfolio_revenue_chf)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono">{formatNumber(r.portfolio_margin_chf)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono font-semibold text-indigo-900">
                  {formatNumber(r.kpi_contribution_chf)}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 align-top text-gray-600">
                  {r.deal_closure_date ? String(r.deal_closure_date).slice(0, 10) : '—'}
                </td>
                <td className="px-2 py-1.5 text-right align-top text-gray-600">{r.payment_terms_days}</td>
                <td className="whitespace-nowrap px-2 py-1.5 align-top text-gray-500">{formatIsoDate(r.forecast_stamp_at)}</td>
                <td className="whitespace-nowrap px-2 py-1.5 align-top text-[10px] text-gray-500">
                  {r.fx_used?.source === 'forecast_stamp' ? 'Stamp' : 'Deal'}
                </td>
                <td className="px-2 py-1.5 text-right align-top font-mono text-[10px]">{r.fx_used?.eur_chf?.toFixed(4)}</td>
                <td className="px-2 py-1.5 text-right align-top font-mono text-[10px]">{r.fx_used?.usd_chf?.toFixed(4)}</td>
                <td className="whitespace-nowrap px-2 py-1.5 align-top text-[10px] text-gray-500">
                  {r.revenue_basis === 'stamped_forecast_chf' ? 'Stamped' : 'Totals'}
                </td>
                <td className="px-2 py-1.5 align-top text-[10px] leading-snug text-gray-600">{r.inclusion_reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ForecastKpiExplainSheet({ open, onOpenChange, data, loading, cardTotalChf, titleOverride, onOpenDeal }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden rounded-none border-0 p-0 touch-manipulation sm:h-full sm:max-h-none sm:rounded-l-lg sm:border-l sm:border-gray-200 md:max-w-none md:w-[min(88rem,min(100vw,100dvw))] xl:w-[min(96rem,min(100vw,100dvw))]"
        data-testid="forecast-kpi-explain-sheet"
      >
        <div className="border-b border-gray-100 px-[max(1rem,env(safe-area-inset-left))] pb-4 pr-14 pt-[max(1rem,env(safe-area-inset-top))] sm:px-6 sm:pr-6 sm:pt-6">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-base leading-snug sm:text-lg">
              {titleOverride || data?.metric_label || 'Forecast KPI'}
            </SheetTitle>
            <SheetDescription className="text-xs leading-relaxed sm:text-sm">
              Explainability / audit trail — same rules and read cap as the Forecast dashboard cards.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden px-[max(1rem,env(safe-area-inset-left))] pb-[max(1rem,env(safe-area-inset-bottom))] pr-[max(1rem,env(safe-area-inset-right))] pt-2 sm:px-6 sm:pb-6">
          {loading && (
            <p className="text-sm text-gray-500" data-testid="forecast-kpi-explain-loading">Loading breakdown…</p>
          )}
          {!loading && data && (
            <>
              <div className="rounded-lg border border-gray-200 bg-slate-50/80 p-3 text-sm text-gray-700 sm:p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">How this KPI is computed</p>
                <ul className="mt-2 list-disc space-y-1.5 pl-4 text-[11px] leading-relaxed text-gray-600 sm:text-xs">
                  {data.formula_lines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-3 text-xs sm:grid-cols-2 sm:p-4 lg:grid-cols-4">
                <div className="min-w-0">
                  <p className="font-semibold uppercase tracking-wider text-gray-400">Dashboard card</p>
                  <p className="mt-1 break-words font-mono text-base font-semibold tabular-nums text-gray-900 sm:text-lg">
                    {formatNumber(cardTotalChf ?? data.kpi_total_chf)} CHF
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold uppercase tracking-wider text-gray-400">Sum (included rows)</p>
                  <p className="mt-1 break-words font-mono text-base font-semibold tabular-nums text-gray-900 sm:text-lg">
                    {formatNumber(data.sum_included_chf)} CHF
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold uppercase tracking-wider text-gray-400">Reconciliation</p>
                  <p className={`mt-1 text-sm font-semibold sm:text-base ${data.reconciles ? 'text-green-700' : 'text-red-600'}`}>
                    {data.reconciles ? 'Matches KPI total' : 'Mismatch — report this'}
                  </p>
                  <p className="mt-0.5 text-[10px] leading-snug text-gray-500">
                    API total {formatNumber(data.kpi_total_chf)} CHF (same engine as the card).
                  </p>
                </div>
                <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                  <p className="font-semibold uppercase tracking-wider text-gray-400">Counts</p>
                  <p className="mt-1 text-gray-700">
                    <span className="font-mono font-semibold">{data.included_deals.length}</span> included
                    <span className="mx-1 text-gray-300">·</span>
                    <span className="font-mono font-semibold">{data.excluded_deals.length}</span> excluded
                  </p>
                  <p className="mt-0.5 text-[10px] leading-snug text-gray-500">
                    Read {data.deals_in_read} / cap {data.read_cap} · DB {data.total_deals_in_database} deals
                  </p>
                </div>
              </div>

              <DealTable title="Included in KPI total" rows={data.included_deals} onOpenDeal={onOpenDeal} />
              <DealTable title="Excluded from KPI total (shown for audit)" rows={data.excluded_deals} onOpenDeal={onOpenDeal} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
